import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { FiltersComponent } from '../../ui-common/filters-component';
import { Order } from '../../interfaces/orders/order';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { ConfigurationState } from '../../services/configuration-state/configuration-state';
import { CorporationRepositoryService } from '../../services/corporation/corporation-repository-service.service';
import { UserGroupRepositoryService } from '../../services/user-group/user-group-repository.service';
import { UserGroupAccessRepositoryService } from '../../services/user-group-access/user-group-access-repository.service';
import { UserRepositoryService } from '../../services/user/user-repository.service';
import { SelectOption } from '../../interfaces/select-option';

@Component({
    selector: 'lib-message-list-filter',
    templateUrl: './message-list-filter.component.html',
    styleUrls: ['./message-list-filter.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MessageListFilterComponent extends FiltersComponent<Order> implements OnInit, OnDestroy {
  @Input() isAdmin: boolean = false;

  protected _corporations: SelectOption[] = [];
  protected _userGroups: SelectOption[] = [];
  protected _users: SelectOption[] = [];

  private corporationID: string = '';

  constructor(
    private configurationState: ConfigurationState,
    private userGroupAccessRepo: UserGroupAccessRepositoryService,
    private usersService: UserRepositoryService,
    private corporationRepo: CorporationRepositoryService,
    private userGroupRepo: UserGroupRepositoryService,
    repo: OrderRepositoryService,
    formBuilder: FormBuilder,
    datePipe: DatePipe) {
      super(
        repo,
        formBuilder,
        datePipe
      )
  }

  ngOnInit() {
    let readByAdmin = this.isAdmin ? false : null;
    let readByClient = !this.isAdmin ? false : null;

    this._filterForm = this.formBuilder.group({
        isVisible: [true],
        orderMessageIsReadByAdmin: [readByAdmin],
        orderMessageIsReadByClient: [readByClient],
        referenceNumber: [null],
        orderMessageText: [null],
        userGroupID: [null],
        userID: [null],
        sortColumn: ["messageDate"],
        sortDirection: ["desc"]
    })

    // Admins can pick any corporation; client users are limited to their own, so it is never part of their form
    if (this.isAdmin) {
      this._filterForm.addControl('corporationID', this.formBuilder.control(null));
    }

    this._defaultParams = this.buildParamsFromForm(this._filterForm);
    
    this.repo.getFirstPage(this._defaultParams);

    if (this.isAdmin) {
      this.corporationRepo.getCorporationsAsSelectOptions()
        .subscribe(corporations => this._corporations = corporations);
    } else {
      this.userGroupAccessRepo.getReadableUserAccesses()
        .subscribe(groups => this._userGroups = groups.map(group => ({
          value: group.hasAccessToUserGroupID,
          label: group.hasAccessToUserGroup?.name ?? ''
        })));

      this.configurationState.getCorporationID().then(corpID => this.corporationID = corpID);
    }
  }

  protected override onClear() {
    super.onClear();
    this._users = [];

    // Admin user groups belong to the chosen corporation
    if (this.isAdmin) {
      this._userGroups = [];
    }
  }

  protected onCorporationChanged(event: MatSelectChange) {
    // The selected user group and user belong to the previous corporation, so they can't carry over
    this._filterForm.get('userGroupID')?.setValue(null);
    this._filterForm.get('userID')?.setValue(null);
    this._userGroups = [];
    this._users = [];

    if (event.value) {
      this.userGroupRepo.getUserGroupsAsSelectOptions(event.value)
        .subscribe(groups => this._userGroups = groups);
    }
  }

  protected onUserGroupChanged(event: MatSelectChange) {
    // The selected user belongs to the previous group, so it can't carry over
    this._filterForm.get('userID')?.setValue(null);
    this._users = [];

    if (event.value) {
      const corporationID = this.isAdmin ? this._filterForm.get('corporationID')?.value : this.corporationID;

      this.usersService.getUsersAsSelectOptions(corporationID, event.value)
        .subscribe(users => this._users = users);
    }
  }

  ngOnDestroy(): void {
    this.repo.clearFilters();
  }
}